import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { View } from 'react-native';

import { getServiceCategoriesQuery } from '@/api/booking/getServiceCategoriesQuery';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import { useSuspenseProfessionalQuery } from '@/hooks/queries/booking/useSuspenseProfessionalQuery';

import { ServiceCategoryBreadcrumb } from '../ServiceCategoryBreadcrumb';
import { styles } from './styles';
import type { ServiceCategoryBreadcrumbContainerProps } from './types';

const BreadcrumbWithData = ({
  testID,
  shopRoute,
  barberIdOrRoute,
  groupId,
  categoryId,
}: ServiceCategoryBreadcrumbContainerProps) => {
  const { data: shop } = useSuspenseQuery(shopDetailQuery(shopRoute));
  const barber = useSuspenseProfessionalQuery({
    shopId: shop.id,
    barberIdOrRoute,
  })?.barber;

  // Read without suspending: the categories are already cached when the
  // client comes from L1, and a deep link shows the professional first. The
  // list reads the same query and handles its failure.
  const { data: categories } = useQuery({
    ...getServiceCategoriesQuery({
      brandId: shop.brandId,
      shopId: shop.id,
      barberId: barber?.id,
    }),
    enabled: Boolean(groupId),
  });
  // An unknown id leaves its item out; the list shows its own error.
  const categoryName = categories?.rows.find(({ id }) => id === groupId)?.name;
  const subcategoryName = categoryId
    ? categories?.rows.find(({ id }) => id === categoryId)?.name
    : undefined;

  return (
    <ServiceCategoryBreadcrumb
      testID={testID}
      barberName={
        barber ? `${barber.firstName} ${barber.lastName}`.trim() : undefined
      }
      barberPhotoUrl={barber?.photos?.[0]?.url}
      categoryName={categoryName}
      subcategoryName={subcategoryName}
    />
  );
};

export const ServiceCategoryBreadcrumbContainer = (
  props: ServiceCategoryBreadcrumbContainerProps,
) => (
  <View style={styles.container}>
    {props.barberIdOrRoute || props.groupId ? (
      <BreadcrumbWithData {...props} />
    ) : (
      <ServiceCategoryBreadcrumb testID={props.testID} />
    )}
  </View>
);
